<script setup lang="ts">
  import CaseDetailHeader from '@/components/app/cases/CaseDetailHeader.vue'
  import CaseDetailOnboarding from '@/components/app/cases/CaseDetailOnboarding.vue'
  import EmailPopover from '@/components/app/email/emailPopover.vue'
  import UnsavedEmailDialog from '@/components/app/email/UnsavedEmailDialog.vue'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import BetaBadge from '@/components/common/BetaBadge.vue'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import NewPageBanner from '@/components/ui/new-page-banner/NewPageBanner.vue'
  import { useEmailPopover } from '@/composables/useEmailPopover'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'

  import { ROUTES } from '@/router/routes'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useUserStore } from '@/store/userStore'
  import { CasesService, type CustomerCase as Case, FlagEnum } from '@/types/generated'
  import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
  import {
    useRouter,
    onBeforeRouteLeave,
    type RouteLocationNormalized,
    type NavigationGuardNext
  } from 'vue-router'

  const router = useRouter()

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const userStore = useUserStore()
  const user = computed(() => userStore.user)
  const lawSpecificMappers = computed(() => caseStore.lawSpecificMappers)
  const nonEmptyLawSpecificMappers = computed(() => caseStore.nonEmptyLawSpecificMappers)
  const newPdfEditorEnabled = useFeatureFlag(FlagEnum.NEW_PDF_EDITOR)

  // Get the global email popover state
  const emailPopover = useEmailPopover()
  const showEmailPopover = ref(false)

  // Local dialog state for email draft navigation guard
  const emailDialogState = {
    show: ref(false),
    onConfirm: ref<(() => void) | null>(null),
    onCancel: ref<(() => void) | null>(null)
  }

  const pendingEmailNavigation = ref<{
    to: RouteLocationNormalized
    next: NavigationGuardNext
  } | null>(null)

  const emailGuardDisabled = ref(false)

  onBeforeRouteLeave(
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      if (emailGuardDisabled.value || !emailPopover.hasUnsavedDraft.value) {
        return next()
      }

      const extractCaseId = (path: string): string | null => {
        const match = path.match(/\/app\/cases\/([^/]+)/)
        return match ? match[1] : null
      }

      const fromCaseId = extractCaseId(from.path)
      const toCaseId = extractCaseId(to.path)

      if (fromCaseId === null || fromCaseId === toCaseId) {
        return next()
      }

      pendingEmailNavigation.value = { to, next }

      emailDialogState.onConfirm.value = () => {
        if (pendingEmailNavigation.value) {
          const { to } = pendingEmailNavigation.value
          pendingEmailNavigation.value = null

          emailGuardDisabled.value = true
          router
            .push(to.fullPath)
            .then(() => {
              emailGuardDisabled.value = false
            })
            .catch(() => {
              emailGuardDisabled.value = false
            })
        }
      }
      emailDialogState.onCancel.value = () => {
        pendingEmailNavigation.value = null
      }
      emailDialogState.show.value = true

      return next(false)
    }
  )

  // Watch for changes to the global state and update our local state
  watch(
    () => emailPopover.isOpen.value,
    (isOpen) => {
      showEmailPopover.value = isOpen
      if (isOpen) {
        // Reset position when it's opened to ensure it's in the right place
        emailPopover.resetPosition()
      }
    }
  )

  // Set initial value
  showEmailPopover.value = emailPopover.isOpen.value

  const pages = computed(() => {
    const caseUuid = caseRecord.value?.uuid
    if (!caseUuid) return []

    const enableLawSpecificData = caseRecord.value?.org?.enable_law_specific_data ?? false

    const tabs = [
      {
        title: () => h('span', 'Allgemein'),
        route: ROUTES.APP.CASES.CASE_DETAIL.OVERVIEW.replace(':uuid', caseUuid)
      }
    ]

    if (enableLawSpecificData) {
      tabs.push({
        title: () => h('span', 'Beteiligte'),
        route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(':uuid', caseUuid).replace(
          '/:entityUuid?',
          ''
        )
      })
    }

    tabs.push({
      title: () => h('span', 'Kommunikation'),
      route: ROUTES.APP.CASES.CASE_DETAIL.COMMUNICATION.replace(':uuid', caseUuid)
    })

    if (newPdfEditorEnabled.isEnabled.value) {
      tabs.push({
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            'Dokumente',
            caseRecord.value?.documents.length
              ? h('span', { class: 'badge-circular' }, `${caseRecord.value?.documents.length}`)
              : null,
            h(BetaBadge)
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.DOCUMENTS.replace(':uuid', caseUuid)
      })
    }

    tabs.push({
      title: () =>
        h('span', { class: 'flex gap-2 items-center' }, [
          'Termine',
          appointmentCount.value
            ? h('span', { class: 'badge-circular' }, `${appointmentCount.value}`)
            : null
        ]),
      route: ROUTES.APP.CASES.CASE_DETAIL.APPOINTMENTS.replace(':uuid', caseUuid)
    })

    if (enableLawSpecificData) {
      tabs.push(
        {
          title: () =>
            h('span', { class: 'flex gap-2 items-center' }, [
              'Rechtsbezogene Daten', // Text for the second page
              nonEmptyLawSpecificMappers.value.length > 0
                ? h(
                    'span',
                    { class: 'badge-circular' },
                    `${nonEmptyLawSpecificMappers.value.length}`
                  )
                : null
            ]),
          route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', caseUuid)
        },
        // {
        //   title: () => h('span', 'Timeline'),
        //   route: ROUTES.APP.CASES.CASE_DETAIL.TIMELINE.replace(':uuid', caseRecord.value?.uuid)
        // },
        {
          title: () => h('span', 'AI Chat'),
          route: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', caseUuid)
        }
      )
    }

    tabs.push({
      title: () => h('span', 'Interne Notizen'),
      route: ROUTES.APP.CASES.CASE_DETAIL.NOTES.replace(':uuid', caseUuid)
    })

    return tabs
  })

  const activePage = computed(() => {
    const currentPath = router.currentRoute.value.fullPath
    return pages.value.find((page) => {
      // For entities page, match both with and without entityUuid
      if (page.route.includes('/entities')) {
        return currentPath.startsWith(page.route)
      }
      return page.route === currentPath
    })
  })
  const appointmentCount = computed(() => {
    const appointments = caseStore.appointments?.length || 0
    const unbookedProposals =
      caseStore.appointmentProposals?.filter((prop) => !prop.booked)?.length || 0
    return appointments + unbookedProposals
  })

  const handleStatusChange = async (newStatus: string) => {
    if (!caseRecord.value) return

    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          state: newStatus
        }
      })

      // Refresh case data
      await caseStore.loadCase(router.currentRoute.value.params.uuid.toString())
    } catch (error) {
      console.error('Failed to update case status:', error)
    }
  }

  const handleDownloadDocuments = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      const response = await CasesService.casesDownloadDocumentsZipRetrieve({
        uuid: caseRecord.value.uuid
      })

      // Create a blob from the response and trigger download
      const blob = new Blob([response as unknown as Blob], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `jupus_export_${caseRecord.value.data?.client?.last_name || 'case'}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading documents:', error)
      // TODO: Show error toast
    }
  }

  const isDeleteDialogOpen = ref(false)

  const handleDeleteCase = async () => {
    if (!caseRecord.value?.uuid) return

    try {
      await CasesService.casesDestroy({
        uuid: caseRecord.value.uuid
      })
      // Navigate to new case page after successful deletion
      router.push(ROUTES.APP.CASES.CASE_OVERVIEW)
    } catch (error) {
      console.error('Error deleting case:', error)
    }
  }

  const onboardingRef = ref(null)
  const showOnboarding = ref(false)

  const handleOnboardingExit = () => {
    localStorage.setItem('case_onboarding_exited', 'true')
    showOnboarding.value = false
  }

  const handleOnboardingFinish = () => {
    localStorage.setItem('case_onboarding_completed', 'true')
    showOnboarding.value = false
  }

  const startOnboarding = () => {
    try {
      if (onboardingRef.value) {
        onboardingRef.value.start()
      } else {
        console.error('Onboarding ref is null')
      }
    } catch (error) {
      console.error('Failed to start onboarding:', error)
    }
  }

  const updateLastActivitySeenAt = async () => {
    if (!caseRecord.value) return

    await CasesService.casesPartialUpdate({
      uuid: caseRecord.value.uuid,
      requestBody: {
        last_activity_seen_at: new Date().toISOString()
      }
    })
  }

  const isLoading = ref(true)

  onMounted(async () => {
    isLoading.value = true

    try {
      await newPdfEditorEnabled.checkFlag()
      await caseStore.loadCase(router.currentRoute.value.params.uuid.toString())
      updateLastActivitySeenAt()
      await userStore.fetchUser()

      // Check if onboarding has been completed or exited
      const onboardingCompleted = localStorage.getItem('case_onboarding_completed')
      const onboardingExited = localStorage.getItem('case_onboarding_exited')

      if (!onboardingCompleted && !onboardingExited) {
        showOnboarding.value = true
        startOnboarding()
        nextTick(() => {
          startOnboarding()
          setTimeout(() => {
            startOnboarding()
          }, 2000)
        })
      }
    } catch (error) {
      console.error('Error loading case details:', error)
    } finally {
      isLoading.value = false
    }
  })

  // Watch both showOnboarding and onboardingRef
  watch([showOnboarding, onboardingRef], ([show, ref]) => {
    if (show && ref) {
      startOnboarding()
    }
  })

  const getTabId = (route: string) => {
    // if (route.includes('cases')) return 'allgemein'
    if (route.includes('entities')) return 'entities'
    if (route.includes('communication')) return 'communication'
    if (route.includes('documents')) return 'documents'
    if (route.includes('appointments')) return 'appointments'
    if (route.includes('law-specific-data')) return 'law-specific-data'
    if (route.includes('timeline')) return 'timeline'
    if (route.includes('chat')) return 'ai-chat'
    return 'allgemein'
  }
</script>
<template>
  <div
    v-if="isLoading"
    class="fixed inset-0 flex items-center justify-center bg-white"
  >
    <div class="flex flex-col items-center gap-6">
      <div class="relative h-12 w-12">
        <div
          class="absolute inset-0 animate-[spin_0.8s_linear_infinite] rounded-full border-[3px] border-purple-100
            border-t-purple-600"
        ></div>
      </div>
    </div>
  </div>
  <SpaCompatComponent v-else-if="caseRecord">
    <div class="min-h-screen bg-gray-50">
      <!-- Add key to force re-render when showOnboarding changes -->
      <CaseDetailOnboarding
        v-if="showOnboarding"
        ref="onboardingRef"
        @exit="handleOnboardingExit"
        @finish="handleOnboardingFinish"
      />
      <div class="mx-auto max-w-7xl">
        <CaseDetailHeader
          v-if="caseRecord"
          :case-data="caseRecord"
          @status-change="handleStatusChange"
          @download-documents="handleDownloadDocuments"
          @delete-case="isDeleteDialogOpen = true"
        />
      </div>

      <!-- Responsive Tab Bar with Horizontal Scroll -->
      <div class="relative border-b border-gray-200 dark:border-neutral-700">
        <div class="scrollbar-hide overflow-x-auto px-2 sm:px-0">
          <nav class="flex min-w-max gap-x-2 whitespace-nowrap pt-1">
            <button
              v-for="(page, index) in pages"
              :key="page.route"
              :data-tab="getTabId(page.route)"
              class="inline-flex cursor-pointer items-center gap-x-1 whitespace-nowrap py-2 text-xs hover:text-purple-600
                focus:text-purple-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50 md:gap-x-2
                md:text-sm"
              :class="[
                page.route === activePage?.route
                  ? 'border-b-2 border-purple-600 font-semibold text-purple-600'
                  : '',
                index === 0 ? 'pl-2 pr-1' : 'px-1'
              ]"
              @click="router.push(page.route)"
            >
              <component :is="page.title" />
            </button>
          </nav>
        </div>

        <!-- Fade indicators for scroll -->
        <div
          class="pointer-events-none absolute bottom-0 left-0 top-0 w-8 bg-gradient-to-r from-gray-50 to-transparent
            md:hidden"
        ></div>
        <div
          class="pointer-events-none absolute bottom-0 right-0 top-0 w-8 bg-gradient-to-l from-gray-50 to-transparent
            md:hidden"
        ></div>
      </div>

      <div class="py-3 md:py-5">
        <router-view></router-view>
      </div>
      <EmailPopover v-model:isOpen="showEmailPopover" />
    </div>
  </SpaCompatComponent>
  <AlertDialog v-model:open="isDeleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Sind Sie sicher?</AlertDialogTitle>
        <AlertDialogDescription>
          Diese Aktion kann nicht rückgängig gemacht werden. Die Anfrage und alle zugehörigen Daten
          werden dauerhaft gelöscht.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="isDeleteDialogOpen = false">Abbrechen</AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700"
          @click="handleDeleteCase"
        >
          Löschen
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  <UnsavedEmailDialog
    :show="emailDialogState.show.value"
    @update:show="emailDialogState.show.value = $event"
    @confirm="emailDialogState.onConfirm?.value?.()"
    @cancel="emailDialogState.onCancel?.value?.()"
  />
  <NewPageBanner
    mode="case-detail"
    :case-id="caseRecord?.id"
  />
</template>

<!-- Add this style for hiding scrollbars but maintaining functionality -->
<style>
  .scrollbar-hide {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  .badge-circular {
    @apply inline-flex h-4 items-center justify-center overflow-hidden rounded-full bg-purple-200 p-1 text-[0.6rem] font-medium text-purple-700;
  }
</style>
